import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EASE = [0.16, 1, 0.3, 1];

const QUOTES = [
  {
    quote: "Rab and I didn\u2019t just build a company together \u2014 we stood by each other when it mattered most. Through the highs, the setbacks, and the moments when everything was on the line, we kept choosing to believe in each other.",
    name: "Brian Tuite", role: "CEO, Zenarate",
    image: "/brian.jpg",
  },
  {
    quote: "Rab believed in me to be CEO before I fully believed in myself. He helped me find the cofounders who could turn my vision into a company.",
    name: "Terry Dohrmann", role: "CEO, Lexafide",
    image: "/terry.jpg",
  },
];

function Testimonial({ q, index }) {
  const flip = index % 2 === 1;
  return (
    <motion.figure
      data-testid={`testimonials-item-${index + 1}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12%" }}
      transition={{ duration: 0.9, ease: EASE }}
      className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16"
    >
      {/* Portrait */}
      <Dialog>
        <DialogTrigger asChild>
          <button
            data-testid={`testimonial-image-${index + 1}`}
            className={`group relative aspect-[3/4] overflow-hidden rounded-sm border border-white/10 bg-elevated lg:col-span-5 ${flip ? "lg:order-2" : ""}`}
            aria-label={`Watch ${q.name}'s story`}
          >
            <img
              src={q.image} alt={q.name} loading="lazy" decoding="async"
              className="h-full w-full object-cover object-top grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-noir/70 via-transparent to-transparent" />
            <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-noir/30 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:border-gold group-hover:bg-gold">
              <Play className="h-5 w-5 translate-x-[1px] fill-cream text-cream transition-colors duration-300 group-hover:fill-noir group-hover:text-noir" />
            </span>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-3xl overflow-hidden rounded-sm border border-white/10 bg-elevated p-0 text-cream shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
          <DialogTitle className="sr-only">{q.name} — video testimonial</DialogTitle>
          <div className="relative aspect-video w-full overflow-hidden bg-noir">
            <img src={q.image} alt={q.name} className="h-full w-full object-cover opacity-40" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold/70 bg-noir/40">
                <Play className="h-6 w-6 translate-x-[1px] fill-gold text-gold" />
              </span>
              <p className="text-[0.7rem] uppercase tracking-[0.24em] text-cream/60">Video testimonial — coming soon</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quote */}
      <figcaption className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
        <blockquote
          data-testid={`testimonial-quote-${index + 1}`}
          className="font-quote leading-[1.12] tracking-tight text-cream"
          style={{ fontSize: "clamp(1.75rem, 3.4vw, 3rem)" }}
        >
          &ldquo;{q.quote}&rdquo;
        </blockquote>
        <div className="mt-9 flex items-center gap-4 border-t border-white/10 pt-6">
          <img
            src={q.image} alt={q.name}
            className="h-12 w-12 rounded-full object-cover object-top grayscale"
          />
          <div>
            <p className="font-medium text-cream">{q.name}</p>
            <p className="mt-0.5 text-sm text-mute">{q.role}</p>
          </div>
        </div>
      </figcaption>
    </motion.figure>
  );
}

export default function Testimonials() {
  return (
    <section
      id="testimonials" data-testid="testimonials-section"
      className="relative scroll-mt-24 border-t border-white/10 bg-noir py-24 md:py-32 lg:py-40"
    >
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          className="mb-20 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
        >
          05 &mdash; In Their Words
        </motion.p>
        <div className="flex flex-col gap-24 md:gap-32">
          {QUOTES.map((q, i) => (
            <Testimonial key={q.name} q={q} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
